export function isEmpty<T extends object>(obj: T | null | undefined): boolean {
  return obj != null && Object.keys(obj).length === 0
}

export function shallowEqual(obj1: Record<string, unknown>, obj2: Record<string, unknown>) {
  if (obj1 === obj2) return true

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) return false

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false
    }
  }

  return true
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export const random4Digits = () => Math.floor(Math.random() * 10000)

export const createId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id_${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`

export const formatTime = (timestamp: number) =>
  new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

const rtf = new Intl.RelativeTimeFormat(navigator.language, { numeric: 'auto' })

// Coarse relative time ("today", "yesterday", "5 days ago", "last month",
// "2 years ago"). Uses the platform's Intl.RelativeTimeFormat so localisation
// and plural rules come for free.
export const relativeTime = (input: Date | number | string | null | undefined): string => {
  if (!input) return ''
  const date = input instanceof Date ? input : new Date(input)
  if (isNaN(date.getTime())) return ''
  const day = 24 * 60 * 60 * 1000
  const diffDays = Math.floor((Date.now() - date.getTime()) / day)
  if (diffDays < 30) return rtf.format(-diffDays, 'day')
  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 12) return rtf.format(-diffMonths, 'month')
  return rtf.format(-Math.floor(diffMonths / 12), 'year')
}

export const errorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : String(err)

// Split a filename into stem + extension so the stem can truncate with
// ellipsis while the extension stays visible (macOS Finder style).
// ".gitignore" and "README" (no meaningful split) keep the full name as stem.
export const splitName = (name: string): [string, string] => {
  const dot = name.lastIndexOf('.')
  if (dot <= 0) return [name, '']
  return [name.slice(0, dot), name.slice(dot)]
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

// ⚠⚠ 1000, NOT 1024, AND IT IS PAIRED WITH `plans.bandwidth_bytes_free`.
// The labels here are SI — KB, MB, GB — so the divisor has to be SI too. It was 1024, which
// renders GiB under a GB label. That error CANCELLED the database's own: the allowance columns
// held binary quantities in a column `docs/PRICING.md` sells as GB, so 107374182400 rendered as
// "100.0 GB" and the screen agreed with the page by accident.
// Router migration 0139 moves the columns and the SQL divisor to 10^9. This is the third side of
// that change, and there is no version of this function that is right on its own: it is right
// against a database. Until 0139 applies, this file reading 1000 shows 107.4 GB against a page
// selling 100 GB — the same discrepancy in the other direction. MERGE THIS AFTER 0139 IS APPLIED.
const BYTES_PER_UNIT = 1000
export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const i = Math.min(BYTE_UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT)))
  const value = bytes / Math.pow(BYTES_PER_UNIT, i)
  return `${i === 0 ? Math.round(value) : value.toFixed(1)} ${BYTE_UNITS[i]}`
}

export const copyAndOpenUrl = async (text: string, url: string) => {
  if ('clipboard' in navigator) {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Ignore clipboard errors so the URL still opens.
    }
  }
  window.open(url, '_blank', 'noreferrer')
}

export const formatNodeAddress = (node: { name: string }, network?: { name: string }) => {
  return `${node.name.toLowerCase()}.${network?.name.toLowerCase() || ''}`
}

export const pickFields = (obj: Record<string, unknown> | null | undefined, keys: readonly string[]) => {
  if (!obj) return {}
  return keys.reduce<Record<string, unknown>>((acc, key) => {
    if (key in obj) acc[key] = obj[key]
    return acc
  }, {})
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
// Cents → "$1.00". Billing amounts are stored as integer cents (Epic 2).
/** Upper-cases the first character. One definition: the billing panel prints an invoice status
 * beside a payment-method name, and two copies drift into two conventions on one screen. */
export const capitalize = (s: string): string => s.replace(/^\w/, c => c.toUpperCase())

export const formatCents = (cents: number): string => usd.format((Number.isFinite(cents) ? cents : 0) / 100)
